import { defineStore } from 'pinia';
import { PermissionListOut } from '@/client/types';
import { PermissionApiClient, ProductApiClient } from '@/client/requests';


export const useAvailablePermissionsStore = defineStore('availablePermissions', {
  state: () => ({
    availablePermissions: [] as PermissionListOut[],
  }),
  actions: {
    /**
     * Sets the available permissions for the current store instance.
     *
     * @param permissions - An array of permission strings to assign.
     */
    setPermissions(permissions: string[]) {
      this.availablePermissions = permissions;
    },
    /**
     * Removes a permission from the available permissions list by its ID.
     *
     * @param permissionId - The unique identifier of the permission to be removed.
     */
    removePermission(permissionId: string) {
      this.availablePermissions = this.availablePermissions.filter(p => p.id !== permissionId);
    },
    /**
     * Clears all permissions by resetting the `permissions` array to empty.
     *
     * @remarks
     * This method removes all existing permissions from the store.
     */
    clearPermissions() {
      this.permissions = [];
    },
    /**
     * Fetches permissions for a given product by its ID and updates the store.
     *
     * @param productId - The unique identifier of the product whose permissions are to be fetched.
     * @returns A promise that resolves when the permissions have been fetched and set.
     * @throws Logs an error to the console if fetching permissions fails.
     */
    async fetchPermissionsByProductId(productId: string) {
      const productApiClient = new ProductApiClient();
      try {
        const product = await productApiClient.getById(productId);
        this.setPermissions(product.permissions);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    },
    /**
     * Fetches the list of active permissions from the API and updates the store.
     * If the request fails, logs an error message to the console.
     *
     * @returns {Promise<void>} A promise that resolves when the permissions have been fetched and set.
     */
    async fetchActivePermissions() {
      const permissionApiClient = new PermissionApiClient();
      try {
        const permissions = await permissionApiClient.getActivePermissions();
        this.setPermissions(permissions);
      } catch (error) {
        console.error('Failed to fetch active permissions:', error);
      }
    }
  },
});